/**
 * Notification Schedule — full-page create/edit (was a dialog on the tab).
 *
 * Ports the tab's form verbatim: same timing rules (CRON / INTERVAL / ONCE /
 * EVENT), the same code/timing validators, and the same "target is immutable on
 * edit" rule. Only the shell changed — a page with a header and a footer, so it
 * matches every other create/edit screen.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'sharedUi/Calendar';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Input } from 'sharedUi/Input';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import FormField from '../../components/common/form-field';
import { JsonField } from '../../components/common/json-field';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useToastApi } from '../../hooks/use-toast-api';
import { useCanCreate, useCanUpdate } from '../../hooks/usePermissions';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { JOB_NAMES } from '../../constants/jobNames';
import { schedulesService } from '../../services/schedules.service';
import { notificationMastersService } from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import {
  POLL_INTERVAL_SECONDS, blankTiming, fromNaiveIso, toNaiveIso,
} from '../../utils/schedule-timing';
import { browserZone, zoneOptions } from '../../utils/timezones';
import { referencableIn, useAccessibleProjects } from '../../hooks/useProjectScope';
import { ProjectCell } from '../../components/common/project-cell';

const TYPE_OPTIONS = [
  { label: 'Cron', value: 'CRON' },
  { label: 'Interval', value: 'INTERVAL' },
  { label: 'Once', value: 'ONCE' },
  { label: 'Event', value: 'EVENT' },
];

interface FormState {
  scheduleCode: string;
  masterId: string;
  scheduleType: string;
  cronExpression?: string | null;
  intervalSeconds?: number | null;
  runAtDateTime?: string | null;
  eventKey?: string | null;
  timezone?: string | null;
  startDateTime?: string | null;
  endDateTime?: string | null;
  isScheduleEnabled: boolean;
  parameters: Record<string, unknown>;
}

const EMPTY: FormState = {
  scheduleCode: '',
  masterId: '',
  scheduleType: 'CRON',
  cronExpression: '0 9 * * *',
  timezone: browserZone(),
  isScheduleEnabled: false,
  parameters: {},
};

/* The event-key naming standard: dot-separated UPPER_SNAKE segments, at least
   two — `UNIT.UPDATE`, `WHATSAPP.REPLY_RECEIVED`, `ERR.CORE.WHATSAPP.SEND.FAILED`.
   Not decoration: an event is matched by EXACT string, so a key that differs only
   in case from what a producer publishes silently matches nothing, and the
   schedule looks configured while never firing. */
const EVENT_KEY_SHAPE = /^[A-Z][A-Z0-9_]*(\.[A-Z][A-Z0-9_]*)+$/;
const EVENT_KEY_HINT =
  'Use UPPERCASE dot-separated segments, at least two — e.g. UNIT.UPDATE. '
  + 'Letters, digits and underscores only.';

const ScheduleForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'schedule', basePath: '/schedules' });
  const isEdit = !!id;
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const canCreate = useCanCreate(JOB_NAMES.SCHEDULE_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.SCHEDULE_MASTER);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitted, setSubmitted] = useState(false);

  const ZONES = useMemo(() => zoneOptions(form.timezone), [form.timezone]);

  // The notifications a schedule can point at, named by their type's code.
  const mastersQ = useQuery({
    queryKey: ['notification-masters', 'for-schedules'],
    queryFn: () => notificationMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 60_000,
  });
  const typesQ = useQuery({
    queryKey: ['notification-types', 'for-schedules'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 60_000,
  });
  /* Only the notifications this schedule may actually point at.

     A schedule does not carry a project of its own — it INHERITS one from its
     target — and the service resolves that target against the CALLER's project:
     its own rows and shared ones, never another project's. The list, though,
     comes back scoped to everything the viewer may see, so without this an
     administrator with several projects is offered a notification the save then
     refuses as "does not exist in this scope". */
  const projectId = localStorage.getItem('appId');
  const masterOptions = useMemo(() => {
    const byId = new Map((typesQ.data?.data ?? []).map((t) => [String(t._id), t]));
    return (mastersQ.data?.data ?? [])
      .filter(referencableIn(projectId))
      .map((m) => ({
        value: String(m._id),
        label: byId.get(String(m.notificationTypeId))?.notificationCode || String(m._id),
      }));
  }, [mastersQ.data, typesQ.data, projectId]);

  /* Which project this schedule belongs to. Derived, never chosen: a schedule
     carries no appId of its own — it inherits the target's — so the notification
     above decides, and this shows what that decision is before saving. The
     background-job schedule form states the same thing about its job. */
  const { projects, canScopeByProject } = useAccessibleProjects();
  const masterAppId = useMemo(
    () => (mastersQ.data?.data ?? []).find((m) => String(m._id) === form.masterId)?.appId ?? null,
    [mastersQ.data, form.masterId],
  );

  // Load the schedule in edit mode.
  const scheduleQ = useQuery({
    queryKey: ['engine-schedule', id],
    queryFn: () => schedulesService.getById(id!),
    enabled: isEdit && isReady && !!id,
  });
  useBreadcrumbTitle(isEdit ? scheduleQ.data?.scheduleCode : null);

  useEffect(() => {
    const s = scheduleQ.data;
    if (s && isEdit) {
      setForm({
        scheduleCode: s.scheduleCode || '',
        masterId: String(s.masterId || ''),
        scheduleType: s.scheduleType || 'CRON',
        cronExpression: s.cronExpression,
        intervalSeconds: s.intervalSeconds,
        runAtDateTime: s.runAtDateTime,
        eventKey: s.eventKey,
        timezone: s.timezone || browserZone(),
        startDateTime: (s as { startDateTime?: string | null }).startDateTime,
        endDateTime: (s as { endDateTime?: string | null }).endDateTime,
        isScheduleEnabled: !!s.isScheduleEnabled,
        parameters: (s.parameters as Record<string, unknown>) || {},
      });
    }
  }, [scheduleQ.data, isEdit]);

  /* The target is NOT defaulted to the first notification. Whichever happens to
     sort first is not a choice anybody made, and a schedule saved against it
     fires the wrong notification while looking deliberate — the background-job
     form asks for its job outright for the same reason. */

  /** Timing fields are mutually exclusive — switching type clears the others. */
  const changeType = (scheduleType: string) =>
    setForm((f) => ({ ...f, scheduleType, ...blankTiming(scheduleType) }));

  // Mirrors the server validators, so a bad value is caught before the round trip.
  // See EVENT_KEY_SHAPE for the naming standard itself.
  const timingError = useMemo(() => {
    if (form.scheduleType === 'CRON' && !form.cronExpression?.trim()) return 'A cron expression is required.';
    if (form.scheduleType === 'EVENT') {
      const key = form.eventKey?.trim();
      if (!key) return 'An event key is required.';
      if (!EVENT_KEY_SHAPE.test(key)) return EVENT_KEY_HINT;
    }
    if (form.scheduleType === 'INTERVAL') {
      if (!form.intervalSeconds) return 'An interval is required.';
      if (form.intervalSeconds < POLL_INTERVAL_SECONDS) {
        return `The interval must be at least the poll interval (${POLL_INTERVAL_SECONDS}s) — anything shorter cannot fire on time.`;
      }
    }
    if (form.scheduleType === 'ONCE') {
      if (!form.runAtDateTime) return 'A run-at date and time is required.';
      const at = fromNaiveIso(form.runAtDateTime);
      if (at && at.getTime() <= Date.now()) return 'The run-at time must be in the future.';
    }
    const start = fromNaiveIso(form.startDateTime);
    const end = fromNaiveIso(form.endDateTime);
    if (start && end && end.getTime() <= start.getTime()) return 'The end date must be after the start date.';
    return '';
  }, [form]);

  const codeError = !/^[A-Z0-9_]{2,64}$/.test(form.scheduleCode || '')
    ? 'Uppercase letters, digits and underscore (2–64).' : '';
  const invalid = !form.masterId || !!codeError || !!timingError;

  const saveM = useMutation({
    mutationFn: (body: Record<string, unknown>) => (isEdit && id
      ? schedulesService.update(id, body)
      : schedulesService.create({ ...body, masterType: 'NOTIFICATION' } as never)),
    onSuccess: () => {
      ok(isEdit ? 'Schedule updated' : 'Schedule created');
      qc.invalidateQueries({ queryKey: ['engine-schedules'] });
      setTimeout(() => navigateToList(), 600);
    },
    onError: (e) => fail(e),
  });

  const submit = () => {
    setSubmitted(true);
    if (invalid) return;
    saveM.mutate({
      scheduleCode: form.scheduleCode.toUpperCase(),
      // Only on create — the update schema rejects a target (repointing is a
      // delete + create, since it changes which permission governs it).
      ...(isEdit ? {} : { masterId: form.masterId }),
      scheduleType: form.scheduleType,
      cronExpression: form.scheduleType === 'CRON' ? form.cronExpression : null,
      intervalSeconds: form.scheduleType === 'INTERVAL' ? form.intervalSeconds : null,
      runAtDateTime: form.scheduleType === 'ONCE' ? form.runAtDateTime : null,
      eventKey: form.scheduleType === 'EVENT' ? form.eventKey : null,
      timezone: form.scheduleType === 'CRON' ? form.timezone : null,
      startDateTime: form.startDateTime || null,
      endDateTime: form.endDateTime || null,
      isScheduleEnabled: form.isScheduleEnabled,
      parameters: form.parameters || {},
    });
  };

  const saving = saveM.isPending;
  const canSave = isEdit ? canUpdate : canCreate;

  if (isEdit && scheduleQ.isLoading) return <PageSkeleton />;

  return (
    <PermissionGuard jobName={JOB_NAMES.SCHEDULE_MASTER}>
      {toast}
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-Schedule-Form">
        {/* Header */}
        <ScreenHeader icon="pi pi-calendar" title={isEdit ? 'Edit Schedule' : 'Create New Schedule'} subtitle="When each notification fires" onBack={() => navigateToList()} />

        <div className="ds-card p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField className="md:col-span-2" label="Notification" required
              hint={isEdit ? 'A schedule cannot be moved to another notification.' : undefined}>
              <Dropdown value={form.masterId} options={masterOptions}
                optionLabel="label" optionValue="value" className="w-full" filter resetFilterOnHide
                disabled={isEdit}
                onChange={(e: { value: string }) => setForm((f) => ({ ...f, masterId: e.value }))} />
            </FormField>

            {canScopeByProject && (
              <FormField className="md:col-span-2" label="Project"
                hint="Taken from the notification above — a schedule is scoped through what it fires.">
                <div className="flex h-[38px] items-center">
                  {form.masterId
                    ? <ProjectCell appId={masterAppId} projects={projects} />
                    : <span className="text-sm text-ink-muted">Pick a notification first.</span>}
                </div>
              </FormField>
            )}

            <FormField label="Schedule code" required
              hint="Unique per notification and type — one can hold several."
              error={submitted ? codeError : ''}>
              <Input name="scheduleCode" value={form.scheduleCode} className="w-full"
                placeholder="DAILY_DIGEST"
                onChange={(v: string) => setForm((f) => ({ ...f, scheduleCode: v.toUpperCase() }))} />
            </FormField>
            <FormField label="Type" required hint="Changing this clears the timing below.">
              <Dropdown value={form.scheduleType} options={TYPE_OPTIONS}
                optionLabel="label" optionValue="value" className="w-full"
                onChange={(e: { value: string }) => changeType(e.value)} />
            </FormField>

            <div className="md:col-span-2 rounded-md border border-gray-200 p-3 dark:border-gray-700">
              {form.scheduleType === 'CRON' && (
                <>
                  <FormField label="Cron expression" required
                    hint={<>min hour day month weekday — <code>0 9 * * *</code> is 9am daily.</>}>
                    <Input name="cronExpression" value={form.cronExpression || ''} className="w-full"
                      placeholder="0 9 * * *"
                      onChange={(v: string) => setForm((f) => ({ ...f, cronExpression: v }))} />
                  </FormField>
                  <FormField label="Timezone"
                    hint="The wall clock the expression is read in. The zone is stored, so it keeps firing at the stated local time across daylight saving.">
                    <Dropdown value={form.timezone || browserZone()} options={ZONES}
                      optionLabel="label" optionValue="value" filter filterBy="label" className="w-full"
                      onChange={(e: { value: string }) => setForm((f) => ({ ...f, timezone: e.value }))} />
                  </FormField>
                </>
              )}
              {form.scheduleType === 'INTERVAL' && (
                <FormField label="Every (seconds)" required
                  hint="Shorter than the poll interval will not fire any faster.">
                  <InputNumber value={form.intervalSeconds ?? 300} min={POLL_INTERVAL_SECONDS} showButtons
                    className="w-full num-compact" useGrouping={false}
                    onValueChange={(e) => setForm((f) => ({ ...f, intervalSeconds: e.value ?? undefined }))} />
                </FormField>
              )}
              {form.scheduleType === 'ONCE' && (
                <FormField label="Run at" required
                  hint="Fires once, then disables itself — shown as “Completed”.">
                  <Calendar value={fromNaiveIso(form.runAtDateTime)} showTime showIcon hourFormat="24"
                    dateFormat="dd M yy" className="w-full"
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    onChange={(e) => setForm((f) => ({ ...f, runAtDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
                </FormField>
              )}
              {form.scheduleType === 'EVENT' && (
                <FormField label="Event key" required
                  hint={'Fires whenever this is announced — no clock. UPPERCASE, dot-separated, '
                        + 'e.g. UNIT.UPDATE or ERR.CORE.WHATSAPP.SEND.FAILED'}>
                  {/* Upper-cased as it is typed rather than rejected afterwards:
                      matching is EXACT, so `unit.update` and `UNIT.UPDATE` are two
                      different events, and the mistake is invisible — the schedule
                      looks configured and simply never fires. The hint used to show
                      a lowercase example, which is how the one non-conforming key in
                      the system got its name. */}
                  <Input name="eventKey" value={form.eventKey || ''} className="w-full"
                    placeholder="UNIT.UPDATE"
                    onChange={(v: string) => setForm((f) => ({
                      ...f, eventKey: v.toUpperCase().replace(/[^A-Z0-9_.]/g, ''),
                    }))} />
                </FormField>
              )}
              {(submitted || isEdit) && timingError
                && <div className="mt-2 text-xs text-red-500">{timingError}</div>}
            </div>

            {form.scheduleType !== 'EVENT' && (
              <>
                {form.scheduleType !== 'ONCE' && (
                  <FormField label="Start (optional)" hint="Before this, the schedule is dormant.">
                    <Calendar value={fromNaiveIso(form.startDateTime)} showTime showIcon hourFormat="24"
                      dateFormat="dd M yy" className="w-full"
                      appendTo={typeof document !== 'undefined' ? document.body : undefined}
                      onChange={(e) => setForm((f) => ({ ...f, startDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
                  </FormField>
                )}
                <FormField label="End (optional)" hint="After this, it stops firing for good.">
                  <Calendar value={fromNaiveIso(form.endDateTime)} showTime showIcon hourFormat="24"
                    dateFormat="dd M yy" className="w-full"
                    appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    onChange={(e) => setForm((f) => ({ ...f, endDateTime: toNaiveIso((e.value as Date) ?? null) }))} />
                </FormField>
              </>
            )}

            <FormField className="md:col-span-2" label="Enabled"
              hint="Enabling recomputes the next run from now — a paused week does not fire a backlog.">
              <InputSwitch checked={form.isScheduleEnabled}
                onChange={(e) => setForm((f) => ({ ...f, isScheduleEnabled: !!e.value }))} />
            </FormField>

            <JsonField className="md:col-span-2" label="Parameters"
              hint="The values the templates render. A clock-driven fire has no payload, so anything the notification declares has to come from here."
              value={form.parameters}
              onChange={(o) => setForm((f) => ({ ...f, parameters: o || {} }))} />
          </div>

          {/* Footer */}
          <div className="mt-4 px-4 py-3 -mx-4 -mb-4 bg-canvas dark:bg-gray-900/50 border-t border-line flex justify-end gap-2 rounded-b-lg">
            <Button label="Cancel" severity="secondary" outlined size="small" onClick={() => navigateToList()} disabled={saving} />
            {canSave && (
              <Button label={saving ? 'Saving...' : isEdit ? 'Save' : 'Create'}
                icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                size="small" onClick={submit} disabled={saving} testId="ScheduleForm.Button.Save" />
            )}
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default ScheduleForm;
